import React from 'react';
import {useTranslation} from 'react-i18next';
import {IV_DOCUMENT_IMAGE_TYPES, LANGUAGE_CODES} from '@chekin/core';
import {getDocumentType, useIVStore} from '../store';

export enum IV_DOCUMENT_ANIMATIONS {
  frontside = 'frontside',
  backside = 'backside',
  passport = 'passport',
  selfie = 'selfie',
  ocr = 'ocr',
}

const getLanguage = (lang: string) => {
  const mappedLang = lang as LANGUAGE_CODES;
  return ![LANGUAGE_CODES.en, LANGUAGE_CODES.it, LANGUAGE_CODES.es].includes(mappedLang)
    ? LANGUAGE_CODES.en
    : mappedLang;
};

const useFetchIVLottiesDefault = {
  type: IV_DOCUMENT_IMAGE_TYPES.front,
  isOCR: false,
};

type useFetchIVLottieProps = {
  type?: IV_DOCUMENT_IMAGE_TYPES;
  isOCR?: boolean;
};
function useFetchIVLottie({
  type,
  isOCR,
}: useFetchIVLottieProps = useFetchIVLottiesDefault) {
  const {i18n} = useTranslation();
  const documentType = useIVStore(getDocumentType);
  const [animationData, setAnimationData] = React.useState<object | null>(null);

  const getLottieFileName = React.useCallback(() => {
    const language = getLanguage(i18n.language);

    const IV_LOTTIES_BY_LANGUAGE: Record<IV_DOCUMENT_ANIMATIONS, string> = {
      [IV_DOCUMENT_ANIMATIONS.frontside]: `${language}-instructions-id-front`,
      [IV_DOCUMENT_ANIMATIONS.backside]: `${language}-instructions-id-back`,
      [IV_DOCUMENT_ANIMATIONS.passport]: `${language}-instructions-passport`,
      [IV_DOCUMENT_ANIMATIONS.selfie]: `${language}-instructions-selfie`,
      [IV_DOCUMENT_ANIMATIONS.ocr]: `${language}-instructions-orc`,
    };

    if (isOCR) {
      return IV_LOTTIES_BY_LANGUAGE[IV_DOCUMENT_ANIMATIONS.ocr];
    }

    switch (type) {
      case IV_DOCUMENT_IMAGE_TYPES.front:
        return documentType?.two_sides
          ? IV_LOTTIES_BY_LANGUAGE[IV_DOCUMENT_ANIMATIONS.frontside]
          : IV_LOTTIES_BY_LANGUAGE[IV_DOCUMENT_ANIMATIONS.passport];
      case IV_DOCUMENT_IMAGE_TYPES.back:
        return IV_LOTTIES_BY_LANGUAGE[IV_DOCUMENT_ANIMATIONS.backside];
      case IV_DOCUMENT_IMAGE_TYPES.selfie:
        return IV_LOTTIES_BY_LANGUAGE[IV_DOCUMENT_ANIMATIONS.selfie];
      default:
        return IV_LOTTIES_BY_LANGUAGE[IV_DOCUMENT_ANIMATIONS.frontside];
    }
  }, [i18n.language, type, documentType?.two_sides, isOCR]);

  React.useEffect(
    function lazyPreloadLottie() {
      const lottieFileName = getLottieFileName();
      import(`../assets/lotties/iv/${lottieFileName}.json`).then(data => {
        setAnimationData(data?.default);
      });
    },
    [getLottieFileName],
  );

  return {animationData};
}

export {useFetchIVLottie};
